import { cn } from "../lib/cn";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Optional count shown in mono after the label. */
  count?: number | string;
  countTone?: "neutral" | "err" | "warn" | "ok";
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: SegmentedOption<T>[];
  "aria-label": string;
  size?: "md" | "lg";
  className?: string;
}

const countTone = {
  neutral: "text-fg-subtle",
  err: "text-err",
  warn: "text-warn-fg",
  ok: "text-ok-fg",
};

/** The All / Failed / Scheduled style filter. Radiogroup semantics, arrow keys move selection. */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  size = "md",
  className,
  ...aria
}: SegmentedControlProps<T>) {
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const i = options.findIndex((o) => o.value === value);
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      onValueChange(options[(i + 1) % options.length]?.value ?? value);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      onValueChange(options[(i - 1 + options.length) % options.length]?.value ?? value);
    }
  };
  return (
    <div
      role="radiogroup"
      aria-label={aria["aria-label"]}
      onKeyDown={onKeyDown}
      className={cn(
        "inline-flex gap-0.5 rounded-[8px] bg-surface-inset p-0.5 text-sm font-medium",
        className,
      )}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onValueChange(o.value)}
            className={cn(
              "flex items-center gap-1.5 rounded-[6px] px-2.5 transition-colors",
              size === "lg" ? "h-[28px]" : "h-[24px]",
              active
                ? "bg-surface text-fg shadow-[0_1px_1px_rgba(16,22,31,0.08)]"
                : "text-fg-muted hover:text-fg",
            )}
          >
            {o.label}
            {o.count !== undefined ? (
              <span
                className={cn(
                  "font-mono",
                  active ? countTone[o.countTone ?? "neutral"] : "text-fg-subtle",
                )}
              >
                {o.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
